/**
 * <dropit-carousel> — a dependency-free carousel on top of native scroll-snap.
 *
 * Markup:
 *   <dropit-carousel data-interval="5000" data-loop>
 *     <div data-track> ...slides (direct children)... </div>
 *     <button data-prev></button> <button data-next></button>   (optional)
 *     <button data-segment> ...one per slide... </button>          (optional)
 *   </dropit-carousel>
 *
 * Swipe, trackpad and keyboard scrolling are handled by the browser. This element only adds
 * arrows, autoplay (paused on hover, focus, touch, off-screen, hidden tab and reduced motion)
 * and segmented progress bars that fill for `data-interval` ms.
 */
class DropitCarousel extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #visible = true;
  #observer = null;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.segments = Array.from(this.querySelectorAll('[data-segment]'));
    this.interval = Number(this.dataset.interval) || 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.style.setProperty('--dropit-carousel-interval', `${this.interval}ms`);

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.#index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.#index + 1, true));
    this.segments.forEach((segment, i) => segment.addEventListener('click', () => this.go(i, true)));

    let scrollFrame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          this.#syncIndex();
          this.#syncEdges();
        });
      },
      { passive: true }
    );

    const pause = () => {
      this.#paused = true;
      this.#stop();
    };
    const resume = () => {
      this.#paused = false;
      this.#schedule();
    };
    this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && pause());
    this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && resume());
    // keyboard focus pauses; a mouse click on an arrow must not freeze autoplay
    this.addEventListener('focusin', (event) => event.target.matches(':focus-visible') && pause());
    this.addEventListener('focusout', resume);
    this.track.addEventListener('touchstart', pause, { passive: true });
    this.track.addEventListener('touchend', () => setTimeout(resume, 4000), { passive: true });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : this.#schedule()));

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#visible ? this.#schedule() : this.#stop();
    });
    this.#observer.observe(this);

    // With several cards in view the last ones never reach the start edge: only show
    // a progress segment for positions the track can actually snap to.
    const syncSegments = () => {
      this.segments.forEach((segment, i) => (segment.hidden = i > this.lastIndex));
      this.#syncEdges();
    };
    syncSegments();
    new ResizeObserver(syncSegments).observe(this.track);

    this.#render();
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
  }

  /** Index of the last slide that can actually snap to the start edge. */
  get lastIndex() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const start = this.slides[0]?.offsetLeft ?? 0;
    const reachable = this.slides.findIndex((slide) => slide.offsetLeft - start >= maxScroll - 2);
    return reachable === -1 ? this.slides.length - 1 : reachable;
  }

  go(index, fromUser = false) {
    const last = this.lastIndex;
    if (index > last) index = this.hasAttribute('data-loop') || !fromUser ? 0 : last;
    if (index < 0) index = this.hasAttribute('data-loop') ? last : 0;

    const start = this.slides[0].offsetLeft;
    this.track.scrollTo({
      left: this.slides[index].offsetLeft - start,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
    this.#index = index;
    this.#render();
  }

  #syncIndex() {
    const start = this.slides[0].offsetLeft;
    const left = this.track.scrollLeft;
    let nearest = 0;
    let best = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - start - left);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    if (left >= this.track.scrollWidth - this.track.clientWidth - 2) nearest = Math.max(nearest, this.lastIndex);
    if (nearest !== this.#index) {
      this.#index = nearest;
      this.#render();
    }
  }

  #render() {
    this.#syncEdges();
    this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.#index));
    this.segments.forEach((segment, i) => {
      segment.toggleAttribute('data-filled', i < this.#index);
      segment.removeAttribute('data-animating');
      segment.setAttribute('aria-current', String(i === this.#index));
    });
    const current = this.segments[this.#index];
    if (current) {
      void current.offsetWidth; // restart the fill animation
      current.setAttribute('data-animating', '');
    }
    this.#schedule();
  }

  /** Flags the ends so arrows that lead nowhere can be hidden: [data-at-start], [data-at-end]. */
  #syncEdges() {
    const left = this.track.scrollLeft;
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.toggleAttribute('data-at-start', left <= 2);
    this.toggleAttribute('data-at-end', left >= max - 2);
    this.toggleAttribute('data-static', max <= 2);
  }

  #schedule() {
    this.#stop();
    if (!this.interval || this.reducedMotion || this.#paused || !this.#visible || document.hidden) {
      this.toggleAttribute('data-paused', true);
      return;
    }
    this.toggleAttribute('data-paused', false);
    this.#timer = setTimeout(() => this.go(this.#index + 1), this.interval);
  }

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('dropit-carousel')) customElements.define('dropit-carousel', DropitCarousel);
